import React, { useState } from "react";
import { FileText, Copy, Check, PlusCircle, CheckCircle2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "../../ui/dialog";

// SCRUM-749: reference for a chronic condition's template prognosis
// options. Two ways to use an option, both non-destructive - neither ever
// replaces or clears what's already in the Prognosis field:
// - Copy: puts the text on the clipboard, Care Manager pastes it wherever
//   (and however edited) they want.
// - Insert (onInsert): appends the option as a new line onto whatever is
//   already in Prognosis - the append itself is the caller's job (it owns
//   the field's current value), this component only ever asks for an
//   option to be inserted, never writes to the field directly.
const PrognosisTemplateReference = ({ options, onInsert }) => {
  const [open, setOpen] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [insertedIndex, setInsertedIndex] = useState(null);

  if (!options || options.length === 0) return null;

  const handleCopy = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedIndex((current) => (current === index ? null : current)), 1500);
    } catch {
      toast.error("Couldn't copy — please select and copy the text manually");
    }
  };

  const handleInsert = (text, index) => {
    onInsert?.(text);
    setInsertedIndex(index);
    toast.success("Added to Prognosis");
    setTimeout(() => setInsertedIndex((current) => (current === index ? null : current)), 1500);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="View prognosis template options"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#48A6C4",
          padding: "2px 4px",
          fontSize: "12px",
          fontWeight: 500,
        }}
      >
        Template
        <FileText size={16} />
      </button>

      <Dialog open={open} setOpen={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Prognosis Template Options</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p style={{ color: "#565975", fontSize: "13px", margin: "0 0 12px" }}>
              Add an option straight to Prognosis, or copy it to paste in yourself
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {options.map((option, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    padding: "10px 12px",
                    border: "1px solid #C9CBD5",
                    borderRadius: "6px",
                    background: "#F8FAFD",
                  }}
                >
                  <span style={{ flex: 1, color: "#0D1139", fontSize: "14px", lineHeight: 1.5 }}>
                    {option}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleInsert(option, i)}
                    title="Add this option to Prognosis"
                    style={{
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: insertedIndex === i ? "#10b981" : "#48A6C4",
                      padding: "2px",
                    }}
                  >
                    {insertedIndex === i ? <CheckCircle2 size={16} /> : <PlusCircle size={16} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopy(option, i)}
                    title="Copy this option"
                    style={{
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: copiedIndex === i ? "#10b981" : "#94A3B8",
                      padding: "2px",
                    }}
                  >
                    {copiedIndex === i ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              ))}
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PrognosisTemplateReference;
